import { useState, useEffect } from 'react';
import { createVenue, postVenueData } from '../../../util/util';
import { getCityOptions, getNeighborhoodOptions } from '../../../util/locations';
import { geocodeAddress } from '../../../util/geocode';
import useFieldErrors, { missingFieldErrors } from '../../../hooks/useFieldErrors';
import ToggleButton from '../ui/ToggleButton';
// import TimeRangePicker from '../pickers/TimeRangePicker';
import TimeRangeSelect from '../pickers/TimeRangeSelect';
import DropdownPicker from '../ui/DropdownPicker';
import Modal from './Modal';
import ModalActions from './ModalActions';
import FormField, { TextField } from '../ui/FormField';
import { useToast } from '../../ui/Toast';

const WORKING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function VenueEditModal({ isModalOpen, setIsModalOpen, selectedItem, orgId, onVenueSaved }) {
  const isEdit = !!selectedItem;
  const { showSuccess, showError } = useToast();
  const { errors, setErrors, clearError } = useFieldErrors();
  const [isSaving, setIsSaving] = useState(false);
  const [workingDays, setWorkingDays] = useState(selectedItem?.working_days || []);
  const [workingHoursOpen, setWorkingHoursOpen] = useState(selectedItem?.working_hours_open?.slice(0, 5) || null);
  const [workingHoursClose, setWorkingHoursClose] = useState(selectedItem?.working_hours_close?.slice(0, 5) || null);
  const [isClosed, setIsClosed] = useState(!!selectedItem?.is_closed);
  const [city, setCity] = useState(selectedItem?.city || null);
  const [neighborhood, setNeighborhood] = useState(selectedItem?.neighborhood || null);
  const [cityOptions, setCityOptions] = useState([]);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);

  useEffect(() => {
    if (!isModalOpen) return;
    let cancelled = false;
    getCityOptions().then(options => {
      if (!cancelled) setCityOptions(options);
    });
    return () => {
      cancelled = true;
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || !city) {
      setNeighborhoodOptions([]);
      return;
    }
    let cancelled = false;
    getNeighborhoodOptions(city).then(options => {
      if (!cancelled) setNeighborhoodOptions(options);
    });
    return () => {
      cancelled = true;
    };
  }, [isModalOpen, city]);

  const toggleWorkingDay = day => {
    setWorkingDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  };

  const handleCityChange = async newCity => {
    setCity(newCity);
    clearError('city');
    const validNeighborhoods = (await getNeighborhoodOptions(newCity)).map(o => o.key);
    if (!validNeighborhoods.includes(neighborhood)) {
      setNeighborhood(null);
    }
  };

  const handleNeighborhoodChange = newNeighborhood => {
    setNeighborhood(newNeighborhood);
    clearError('neighborhood');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;
    const formData = new FormData(e.target);

    const newErrors = missingFieldErrors(formData, ['venueName', 'address1']);
    if (!city) newErrors.city = 'Please select a city';
    if (!neighborhood) newErrors.neighborhood = 'Please select a neighborhood';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const venueData = {
      venue_name: formData.get('venueName'),
      address1: formData.get('address1'),
      neighborhood,
      city,
      metro_area: city,
      working_days: workingDays.length ? workingDays : null,
      working_hours_open: workingHoursOpen,
      working_hours_close: workingHoursClose,
      is_closed: isClosed,
    };

    setIsSaving(true);
    try {
      const needsGeocode =
        !isEdit || venueData.address1 !== selectedItem.address1 || venueData.city !== selectedItem.city;
      const coords = needsGeocode ? await geocodeAddress(venueData.address1, venueData.city) : null;
      if (coords) Object.assign(venueData, coords);

      if (isEdit) {
        await postVenueData({ venue_id: selectedItem.venue_id, ...venueData });
        onVenueSaved?.({ ...selectedItem, ...venueData }, false);
      } else {
        const created = await createVenue({ ...venueData, org_id: orgId });
        if (created) {
          onVenueSaved?.(created, true);
        }
      }
      setIsModalOpen(false);
      if (needsGeocode && !coords) {
        showError("Venue saved, but we couldn't find this address on the map. Please check the address.");
      } else {
        showSuccess('Venue saved.');
      }
    } catch (error) {
      console.error('Error saving venue:', error);
      showError(`Failed to save venue: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => !isSaving && setIsModalOpen(false)}
      title={isEdit ? 'Edit Venue' : 'Add Venue'}
      clipContent={false}
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <TextField
            label="Venue Name"
            name="venueName"
            required
            error={errors.venueName}
            defaultValue={selectedItem?.venue_name || ''}
            onChange={() => clearError('venueName')}
          />

          <TextField
            label="Address"
            name="address1"
            required
            error={errors.address1}
            defaultValue={selectedItem?.address1 || ''}
            onChange={() => clearError('address1')}
          />

          <div className="flex gap-4">
            <FormField label="City" required error={errors.city} className="flex-1">
              <DropdownPicker
                options={cityOptions}
                value={city}
                onChange={handleCityChange}
                placeholder="Select a city"
                width="w-full"
                variant="form"
                borderClass={errors.city ? 'border-secondary-red' : undefined}
              />
            </FormField>

            <FormField label="Neighborhood" required error={errors.neighborhood} className="flex-1">
              <DropdownPicker
                options={neighborhoodOptions}
                value={neighborhood}
                onChange={handleNeighborhoodChange}
                placeholder={city ? 'Select a neighborhood' : 'Select a city first'}
                width="w-full"
                variant="form"
                borderClass={errors.neighborhood ? 'border-secondary-red' : undefined}
              />
            </FormField>
          </div>

          <FormField label="Working Days">
            <div className="join">
              {WORKING_DAYS.map(day => (
                <ToggleButton
                  key={day}
                  active={workingDays.includes(day)}
                  onClick={() => toggleWorkingDay(day)}
                  disabled={isClosed}
                  aria-pressed={workingDays.includes(day)}
                >
                  {day}
                </ToggleButton>
              ))}
            </div>
          </FormField>

          <FormField label="Working Hours" className="w-72">
            {/* <TimeRangePicker
              startValue={workingHoursOpen}
              onStartChange={setWorkingHoursOpen}
              endValue={workingHoursClose}
              onEndChange={setWorkingHoursClose}
              disabled={isClosed}
            /> */}
            <TimeRangeSelect
              startValue={workingHoursOpen}
              onStartChange={setWorkingHoursOpen}
              endValue={workingHoursClose}
              onEndChange={setWorkingHoursClose}
              startPlaceholder="Opens"
              endPlaceholder="Closes"
              defaultHighlight="10:00"
              disabled={isClosed}
            />
          </FormField>
        </div>

        <ModalActions onCancel={() => setIsModalOpen(false)} isLoading={isSaving}>
          <div className="mr-auto">
            <ToggleButton
              isJoinItem={false}
              size="sm"
              inactiveClass="bg-white text-black border-black hover:bg-black hover:text-white"
              active={isClosed}
              disabled={isSaving}
              onClick={() => setIsClosed(prev => !prev)}
              aria-pressed={isClosed}
            >
              Mark venue as closed
            </ToggleButton>
          </div>
        </ModalActions>
      </form>
    </Modal>
  );
}

export default VenueEditModal;
